// SPDX-License-Identifier: AGPL-3.0-or-later

// Shared by the live gateway client and Web Push worker. Both can receive a
// message while a phone is backgrounded, so neither transport owns delivery.
export const NOTIFICATION_HISTORY_CACHE = 'fluxer-notification-history';
const HISTORY_TTL_MS = 24 * 60 * 60 * 1000;
const HISTORY_LIMIT = 256;

type NotificationRegistration = Pick<ServiceWorkerRegistration, 'scope' | 'getNotifications' | 'showNotification'>;
type History = Array<{key: string; at: number}>;

function messageKey(data: unknown): string | null {
	if (!data || typeof data !== 'object') return null;
	const {message_id, channel_id, target_user_id} = data as Record<string, unknown>;
	if (typeof message_id !== 'string' || !message_id || typeof channel_id !== 'string' || !channel_id) return null;
	return JSON.stringify([target_user_id ?? '', channel_id, message_id]);
}

let pending: Promise<void> = Promise.resolve();

export async function showBrowserNotification(
	registration: NotificationRegistration,
	title: string,
	options: NotificationOptions,
	storage: CacheStorage | undefined,
	locks: LockManager | undefined,
): Promise<void> {
	const key = messageKey(options.data);
	if (!key) {
		await registration.showNotification(title, options);
		return;
	}
	const showOnce = async (): Promise<void> => {
		const now = Date.now();
		const historyUrl = new URL('__notification_history__', registration.scope).href;
		let cache: Cache | undefined;
		let history: History = [];
		try {
			cache = await storage?.open(NOTIFICATION_HISTORY_CACHE);
			const response = await cache?.match(historyUrl);
			const stored: unknown = response ? await response.json() : [];
			if (Array.isArray(stored)) {
				history = stored.filter(
					(entry): entry is History[number] =>
						entry && typeof entry.key === 'string' && typeof entry.at === 'number' && entry.at > now - HISTORY_TTL_MS,
				);
			}
		} catch {
			// Storage may be unavailable; still deliver and check visible notifications.
		}
		if (history.some((entry) => entry.key === key)) return;
		try {
			const notifications = await registration.getNotifications();
			if (notifications.some((notification) => messageKey(notification.data) === key)) return;
		} catch {
			// Failure to enumerate notifications must not prevent delivery.
		}
		await registration.showNotification(title, options);
		try {
			// Record only successful displays. Keep dismissed messages too, so a
			// delayed second transport cannot bring them back or alert again.
			await cache?.put(historyUrl, Response.json([...history, {key, at: now}].slice(-HISTORY_LIMIT)));
		} catch {
			// A history write failure must not trigger a second notification.
		}
	};
	if (locks) {
		// Origin-wide: serializes window tabs and the worker, including simultaneous arrivals.
		await locks.request(`konvo-notifications:${registration.scope}`, showOnce);
	} else {
		// Legacy browsers still serialize arrivals within this execution context.
		const operation = pending.then(showOnce);
		pending = operation.catch(() => undefined);
		await operation;
	}
}
